import '../styles/PdfSidebar.scss';
import { useVirtualList } from 'ahooks';
import React, { useEffect, useMemo, Fragment, useRef, useState, useCallback } from 'react';
import classNames from 'classnames';
import { Iconfont } from '@umu/umu-design';
import { PdfOutline } from '../types';
import { TocPage } from './TocPage';
import { perfEnd, perfMeasure, perfStart } from '../utils/perfMetrics';
import { isIOSSafari } from '../utils/common';

const THUMB_ITEM_HEIGHT_PX = 200;
const THUMB_OVERSCAN = 12;
const OUTLINE_INDENT_STEP_PX = 20;
const OUTLINE_NEXT_FALLBACK: PdfOutline = { title: '', items: [], pageNum: Infinity };

interface SidebarProps {
    pdfDoc: any;
    totalPages: number;
    currentPage: number;
    outlines: PdfOutline[];
    showOutlines: boolean;
    className?: string;
    documentTitle?: string;
    onToggleOutline: () => void;
    onCloseOutline: () => void;
    onPageJump: (page: number) => void;
    mainViewportScale?: number; // 主展示区域的 scale
}

interface OutlineItemProps {
    outline: PdfOutline;
    nextOutline: PdfOutline;
    currentPage: number;
    onPageJump: (page: number) => void;
    indentation?: number;
}

function getOutlineKey(outline: PdfOutline): string {
    const title = typeof outline?.title === 'string' ? outline.title : '';
    const pageNum = typeof outline?.pageNum === 'number' ? outline.pageNum : -1;
    return `${pageNum}:${title}`;
}

function isValidPageNum(page: number, totalPages: number): boolean {
    return (
        typeof page === 'number' &&
        Number.isFinite(page) &&
        typeof totalPages === 'number' &&
        Number.isFinite(totalPages) &&
        totalPages > 0 &&
        page >= 1 &&
        page <= totalPages
    );
}

const OutlineItem = React.memo((props: OutlineItemProps) => {
    const { outline, nextOutline, currentPage, onPageJump, indentation = 1 } = props;
    const [isExtracted, setIsExtracted] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);

    const isInCurrentOutline = useMemo(
        () => outline.pageNum <= currentPage && nextOutline.pageNum > currentPage,
        [currentPage, outline.pageNum, nextOutline.pageNum],
    );

    useEffect(() => {
        if (isInCurrentOutline) {
            setIsExtracted(true);
            if (outline.items.length === 0) {
                itemRef.current?.scrollIntoView({ block: 'center' });
            }
        }
    }, [isInCurrentOutline, outline.items.length]);

    const handleToggleExtract = useCallback((e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setIsExtracted((prev) => !prev);
    }, []);

    const handleClickItem = useCallback(() => {
        onPageJump(outline.pageNum);
        setIsExtracted((prev) => !prev);
    }, [onPageJump, outline.pageNum]);

    return (
        <Fragment>
            <div
                ref={itemRef}
                className={classNames('outline-item', {
                    'is-selected': isInCurrentOutline,
                })}
                style={{ paddingLeft: `${indentation * OUTLINE_INDENT_STEP_PX}px` }}
                onClick={handleClickItem}
            >
                <div className="item-row">
                    <Iconfont
                        type="Expand"
                        className={classNames('extract-btn', {
                            'no-child': outline.items.length === 0,
                            'is-extracted': isExtracted,
                        })}
                        onClick={handleToggleExtract}
                    />
                    <div className="item-title">{outline.title}</div>
                </div>
            </div>
            {outline.items.length > 0 &&
                isExtracted &&
                outline.items.map((item, index, arr) => (
                    <OutlineItem
                        key={getOutlineKey(item)}
                        outline={item}
                        nextOutline={arr?.[index + 1] || nextOutline}
                        currentPage={currentPage}
                        onPageJump={onPageJump}
                        indentation={indentation + 1}
                    />
                ))}
        </Fragment>
    );
});

// Safari 限制单次可见区域外预渲染数量，非 Safari 适当扩大预加载范围
const THUMB_ROOT_MARGIN = isIOSSafari ? '200px 0px' : '400px 0px';

const TocThumbItem = React.memo(
    (props: {
        pdfDoc: any;
        pageNum: number;
        onJump: (p: number) => void;
        selected?: boolean;
        mainViewportScale?: number;
        scrollContainerRef: React.RefObject<HTMLDivElement>;
    }) => {
        const { pdfDoc, pageNum, onJump, selected, mainViewportScale, scrollContainerRef } = props;
        const itemRef = useRef<HTMLDivElement>(null);
        // 初始不可见，由 IntersectionObserver 驱动，避免大量 canvas 同时渲染
        const [isVisible, setIsVisible] = useState(false);

        useEffect(() => {
            const el = itemRef.current;
            if (!el) return;
            // root 设为滚动容器，确保在 sidebar 内部滚动时正确计算可见性
            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsVisible(entry.isIntersecting);
                },
                {
                    root: scrollContainerRef.current,
                    threshold: 0,
                    // 提前预渲染：Safari 严格限制 1 屏，其他浏览器可提前 2 屏
                    rootMargin: THUMB_ROOT_MARGIN,
                },
            );
            observer.observe(el);
            return () => observer.disconnect();
        }, [scrollContainerRef]);

        return (
            <div
                ref={itemRef}
                className={classNames('toc-virtual-item', { 'is-selected': selected })}
                style={{ height: `${THUMB_ITEM_HEIGHT_PX}px` }}
            >
                <div className="toc-virtual-item-inner">
                    <TocPage
                        pdfDoc={pdfDoc}
                        pageNum={pageNum}
                        onJump={onJump}
                        visible={isVisible}
                        mainViewportScale={mainViewportScale}
                    />
                </div>
            </div>
        );
    },
);

type ShowMode = 'img' | 'toc';

function usePdfSidebarLogic(props: SidebarProps, exposed: boolean) {
    const { totalPages, showOutlines, onPageJump } = props;
    const [mode, setMode] = useState<ShowMode>('toc');
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const shouldRenderThumbs = exposed && showOutlines && mode === 'img';

    // 一旦首次进入 img 模式，永久保持缩略图挂载，避免切换模式/全屏时重新渲染
    const hasMountedThumbsRef = useRef(false);
    const hasMountedThumbs = hasMountedThumbsRef.current || shouldRenderThumbs;
    if (hasMountedThumbs) hasMountedThumbsRef.current = true;

    const imgOpenPerfStartedRef = useRef(false);
    const imgOpenPerfEndedRef = useRef(false);

    const handleJump = useCallback(
        (page: number) => {
            if (isValidPageNum(page, totalPages)) {
                onPageJump?.(page);
            }
        },
        [onPageJump, totalPages],
    );

    const thumbPages = useMemo(() => {
        if (!hasMountedThumbs || typeof totalPages !== 'number' || totalPages <= 0) {
            return [];
        }
        const t0 = performance.now();
        const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
        perfMeasure('sidebar_thumb_pages_build_ms', performance.now() - t0, 'all', { totalPages });
        return pages;
    }, [shouldRenderThumbs, totalPages]);

    const thumbWrapperRef = useRef<HTMLDivElement>(null);
    const [virtualThumbs] = useVirtualList(thumbPages, {
        containerTarget: scrollContainerRef,
        wrapperTarget: thumbWrapperRef,
        itemHeight: THUMB_ITEM_HEIGHT_PX,
        overscan: THUMB_OVERSCAN,
    });

    const [scrollTop, setScrollTop] = useState(0);
    const rafScrollRef = useRef<number | null>(null);

    useEffect(() => {
        if (!shouldRenderThumbs) {
            return;
        }
        const el = scrollContainerRef.current;
        if (!el) {
            return;
        }

        const onScroll = () => {
            if (rafScrollRef.current !== null) {
                return;
            }
            rafScrollRef.current = requestAnimationFrame(() => {
                rafScrollRef.current = null;
                setScrollTop(el.scrollTop || 0);
            });
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
            if (rafScrollRef.current !== null) {
                cancelAnimationFrame(rafScrollRef.current);
            }
        };
    }, [shouldRenderThumbs]);

    const fallbackThumbs = useMemo(() => {
        if (!shouldRenderThumbs || !thumbPages.length) {
            return [];
        }
        const el = scrollContainerRef.current;
        const viewportH = el?.clientHeight || 0;
        const start = Math.max(0, Math.floor(scrollTop / THUMB_ITEM_HEIGHT_PX) - THUMB_OVERSCAN);
        const end = Math.min(
            thumbPages.length,
            Math.ceil((scrollTop + viewportH) / THUMB_ITEM_HEIGHT_PX) + THUMB_OVERSCAN,
        );
        return Array.from({ length: end - start }, (_, i) => ({ index: start + i, data: start + i + 1 }));
    }, [shouldRenderThumbs, thumbPages.length, scrollTop]);

    const renderThumbs =
        shouldRenderThumbs && thumbPages.length > 0 && virtualThumbs.length === 0 ? fallbackThumbs : virtualThumbs;

    useEffect(() => {
        if (shouldRenderThumbs && thumbPages.length > 0 && virtualThumbs.length === 0) {
            perfMeasure('sidebar_virtual_empty_count', 1, 'all', { totalPages });
        }
    }, [shouldRenderThumbs, totalPages, thumbPages.length, virtualThumbs.length]);

    useEffect(() => {
        if (shouldRenderThumbs && totalPages > 0) {
            if (!imgOpenPerfStartedRef.current) {
                imgOpenPerfStartedRef.current = true;
                imgOpenPerfEndedRef.current = false;
                perfStart('sidebar_img_open', 'all', { totalPages });
            }
        } else {
            imgOpenPerfStartedRef.current = false;
            imgOpenPerfEndedRef.current = false;
        }
    }, [shouldRenderThumbs, totalPages]);

    useEffect(() => {
        if (!shouldRenderThumbs || !imgOpenPerfStartedRef.current || imgOpenPerfEndedRef.current) {
            return;
        }
        if (virtualThumbs.length > 0) {
            imgOpenPerfEndedRef.current = true;
            requestAnimationFrame(() => {
                perfEnd('sidebar_img_open', 'all', { totalPages });
            });
        }
    }, [shouldRenderThumbs, virtualThumbs.length, totalPages]);

    return { mode, setMode, shouldRenderThumbs, hasMountedThumbs, scrollContainerRef, thumbWrapperRef, renderThumbs, handleJump };
}

export const PdfSidebar = React.memo((props: SidebarProps) => {
    const {
        pdfDoc,
        totalPages,
        currentPage,
        outlines,
        showOutlines,
        className = '',
        documentTitle,
        onToggleOutline,
        onCloseOutline,
    } = props;
    // 精确匹配 'full'，避免 'no-full'.includes('full') === true 的误判
    const isShow = typeof className === 'string' && className === 'full';
    const exposed = isShow && showOutlines;

    const { mode, setMode, shouldRenderThumbs, hasMountedThumbs, scrollContainerRef, thumbWrapperRef, renderThumbs, handleJump } =
        usePdfSidebarLogic(props, exposed);
    const isImageMode = mode === 'img';
    const isTocMode = mode === 'toc';

    const programmaticScrollRef = useRef(false);
    const lastUserScrollAtRef = useRef<number>(0);
    const lastAutoScrollPageRef = useRef<number>(0);
    const autoScrollCooldownUntilRef = useRef<number>(0);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) {
            return;
        }
        const onScroll = () => {
            if (!programmaticScrollRef.current) {
                lastUserScrollAtRef.current = performance.now();
            }
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
        };
    }, [scrollContainerRef]);

    const shouldSkipAutoScroll = useCallback(() => {
        const now = performance.now();
        return (
            now < autoScrollCooldownUntilRef.current ||
            (lastUserScrollAtRef.current > 0 && now - lastUserScrollAtRef.current < 800)
        );
    }, []);

    useEffect(() => {
        if (
            !exposed ||
            !isImageMode ||
            !shouldRenderThumbs ||
            !isValidPageNum(currentPage, totalPages) ||
            shouldSkipAutoScroll() ||
            lastAutoScrollPageRef.current === currentPage
        ) {
            return;
        }

        const el = scrollContainerRef.current;
        if (!el) {
            return;
        }
        const idx = currentPage - 1;
        const viewportH = el.clientHeight || 0;
        if (viewportH <= 0) {
            return;
        }

        const top = el.scrollTop || 0;
        const startIndex = Math.floor(top / THUMB_ITEM_HEIGHT_PX);
        const endIndex = Math.floor((top + viewportH) / THUMB_ITEM_HEIGHT_PX);
        if (idx >= startIndex - 1 && idx <= endIndex + 1) {
            return;
        }

        requestAnimationFrame(() => {
            const el2 = scrollContainerRef.current;
            if (!el2) {
                return;
            }
            const vH = el2.clientHeight || 0;
            const targetTop = Math.min(
                Math.max(0, idx * THUMB_ITEM_HEIGHT_PX + THUMB_ITEM_HEIGHT_PX / 2 - vH / 2),
                Math.max(0, (el2.scrollHeight || 0) - vH),
            );

            programmaticScrollRef.current = true;
            try {
                el2.scrollTo({ top: targetTop, behavior: 'auto' });
            } catch {
                el2.scrollTop = targetTop;
            }
            lastAutoScrollPageRef.current = currentPage;
            autoScrollCooldownUntilRef.current = performance.now() + 250;
            requestAnimationFrame(() => {
                programmaticScrollRef.current = false;
            });
        });
    }, [exposed, isImageMode, shouldRenderThumbs, currentPage, totalPages, shouldSkipAutoScroll, scrollContainerRef]);

    const tocScrollRef = useRef<HTMLDivElement>(null);
    const tocLastUserScrollAtRef = useRef<number>(0);
    useEffect(() => {
        const el = tocScrollRef.current;
        if (!el) {
            return;
        }
        const onScroll = () => {
            tocLastUserScrollAtRef.current = performance.now();
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
        };
    }, []);

    useEffect(() => {
        if (!exposed || !isTocMode) {
            return;
        }
        const el = tocScrollRef.current;
        if (!el || (tocLastUserScrollAtRef.current > 0 && performance.now() - tocLastUserScrollAtRef.current < 800)) {
            return;
        }

        const selected = el.querySelector('.outline-item.is-selected') as HTMLElement | null;
        if (!selected) {
            return;
        }
        const c = el.getBoundingClientRect();
        const r = selected.getBoundingClientRect();
        if (r.top < c.top || r.bottom > c.bottom) {
            selected.scrollIntoView({ block: 'center' });
        }
    }, [exposed, isTocMode, currentPage]);

    return (
        <Fragment>
            <div className={classNames('umu-pdf-sidebar', className, { 'outline-sidebar--collapsed': !showOutlines })}>
                <div className="sidebar-header">
                    <div className={classNames('mode-btn-group', { 'img-mode': isImageMode, 'toc-mode': isTocMode })}>
                        <div className="mode-btn" onClick={() => setMode('img')}>
                            <Iconfont type="AddPicture" />
                        </div>
                        <div className="mode-btn" onClick={() => setMode('toc')}>
                            <Iconfont type="UnorderedList" />
                        </div>
                    </div>
                    <Iconfont type="ExpandPage" className="hide-btn" onClick={onCloseOutline} />
                </div>
                <div className="sidebar-content" ref={scrollContainerRef}>
                    <div className={classNames('content-img', { 'is-hidden': !isImageMode })}>
                        {hasMountedThumbs && pdfDoc && totalPages > 0 && (
                            <div ref={thumbWrapperRef} className="toc-virtual-wrapper">
                                {renderThumbs.map((ele) => (
                                    <TocThumbItem
                                        key={`thumb-${ele.data}`}
                                        pdfDoc={pdfDoc}
                                        pageNum={ele.data}
                                        onJump={handleJump}
                                        selected={ele.data === currentPage}
                                        mainViewportScale={props.mainViewportScale}
                                        scrollContainerRef={scrollContainerRef}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={classNames('content-toc', { 'is-hidden': !isTocMode })}>
                        <div className="toc-title">{documentTitle}</div>
                        <div className="toc-list-container" ref={tocScrollRef}>
                            {exposed &&
                                outlines.map((outline, index, arr) => (
                                    <OutlineItem
                                        key={getOutlineKey(outline)}
                                        outline={outline}
                                        nextOutline={arr?.[index + 1] || OUTLINE_NEXT_FALLBACK}
                                        currentPage={currentPage}
                                        onPageJump={handleJump}
                                    />
                                ))}
                        </div>
                    </div>
                </div>
            </div>
            <Iconfont
                type="ExpandPage"
                className={classNames('umu-pdf-sidebar-float-btn', { 'is-hidden': showOutlines }, className)}
                onClick={onToggleOutline}
            />
        </Fragment>
    );
});
